import React, { useEffect, useId, useState } from 'react'

import { useTranslation } from '@disco/i18n/use-translation'

import { Flex, Text, TextField } from 'components/ds'

import {
  formatIPIDisplay,
  formatISNIDisplay,
  IPI_DISPLAY_LENGTH,
  ISNI_DISPLAY_LENGTH,
  parseIPIInput,
  parseISNIInput,
} from '../../utils/identifier-validation'
import InfoPopover from '../info-popover'

type IdentifierType = 'ipi' | 'isni'

type Props = {
  identifierType: IdentifierType
  label: string
  name: string
  value: string
  error?: string | null
  onChange: (value: string) => void
}

const IdentifierTextField = ({
  identifierType,
  label,
  name,
  value,
  error,
  onChange,
}: Props) => {
  const { t } = useTranslation('rights')
  const inputId = useId()
  const formatDisplay =
    identifierType === 'ipi' ? formatIPIDisplay : formatISNIDisplay
  const parseInput = identifierType === 'ipi' ? parseIPIInput : parseISNIInput
  const maxLength =
    identifierType === 'ipi' ? IPI_DISPLAY_LENGTH : ISNI_DISPLAY_LENGTH

  const [displayValue, setDisplayValue] = useState(() => formatDisplay(value))
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(formatDisplay(value))
    }
  }, [value, isFocused, formatDisplay])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    setDisplayValue(raw)
    onChange(parseInput(raw))
  }

  return (
    <Flex direction="column">
      <Flex align="center" gap="1">
        <Text as="label" htmlFor={inputId} size="2" weight="medium">
          {label}
        </Text>
        {identifierType === 'ipi' && (
          <InfoPopover
            label={t('rights:moreInfoAbout', 'More info about {{field}}', {
              field: 'IPI',
            })}
            title={t(
              'rights:ipiInfoTitle',
              'Interested Party Information Number (IPI)',
            )}
          >
            {t(
              'rights:ipiInfoBody',
              'An 11-digit identifier assigned to music rights holders such as composers, lyricists, and publishers. If you enter fewer than 11 digits, leading zeros will be added automatically.',
            )}
          </InfoPopover>
        )}
      </Flex>
      <TextField.Root
        id={inputId}
        name={name}
        value={displayValue}
        maxLength={maxLength}
        size="2"
        color={error ? 'red' : undefined}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />
      {error && (
        <Text size="1" color="red" mt="1">
          {error}
        </Text>
      )}
    </Flex>
  )
}

export default IdentifierTextField
